/**
 * Dashboard Page Component
 * 
 * Main dashboard for authenticated users
 * - Displays upcoming events summary
 * - Shows user's associated riders with stats
 * - Displays leaderboard preview (top 5)
 * - Shows upcoming birthdays
 * - Role-appropriate action buttons
 * 
 * Requirements: 2.1, 4.1, 5.1
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useEventsStore } from '../store/eventsStore';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { DashboardIncidentButton } from '../components/features/incidents/DashboardIncidentButton';
import { restClient, LeaderboardEntry, Rider, Event as ApiEvent, EventType, Group } from '../api/rest-client';
import { formatPosition, formatTime as formatRaceTime } from '../utils/raceResults';
import { formatRiderName } from '../utils/riderDisplay';
import { membersOf } from '../utils/groupMembers';
import { parseExpectedAttendance, getRSVPStatus, updateRSVPStatus, RSVPDisplayStatus } from '../utils/rsvpUtils';
import { filterRidersByEvent } from '../utils/eventFiltering';
import { checkinPath } from '../utils/checkinNav';
import { formatPhoneParenthesized } from '../utils/phone';
import {
  isEventCanceled,
  describeNotificationOutcome,
  describeNotificationFailure,
} from '../utils/eventUpdate';
import { SkillsPanel } from '../components/features/skills/SkillsPanel';
import GroupSkillsCard from '../components/features/skills/GroupSkillsCard';
import TrainingPlanPanel from '../components/features/training-plans/TrainingPlanPanel';
import RaceRegistrationStatus from '@/components/features/riders/RaceRegistrationStatus';
import { useAuthStore } from '../store/authStore';

/**
 * Helper component for rendering leaderboard entries
 */
function LeaderboardSection({ title, entries }: { title: string; entries: LeaderboardEntry[] }) {
  if (entries.length === 0) return null;
  
  return (
    <div>
      <h3 className="text-lg font-semibold text-primary-900 mb-3">{title}</h3>
      <ol className="space-y-3" role="list">
        {entries.map((entry, index) => (
          <li
            key={`${title.toLowerCase()}-${entry.name}-${index}`}
            className="flex items-start justify-between p-3 sm:p-4 bg-primary-50 rounded-lg gap-3"
            data-testid="leaderboard-item"
          >
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className={`
                w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-lg
                ${entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' : ''}
                ${entry.rank === 2 ? 'bg-gray-300 text-gray-800' : ''}
                ${entry.rank === 3 ? 'bg-orange-400 text-orange-900' : ''}
                ${entry.rank > 3 ? 'bg-primary-200 text-primary-800' : ''}
              `} aria-label={`Rank ${entry.rank}`}>
                {entry.rank}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-primary-900 text-base break-words line-clamp-2">
                  {formatRiderName({ name: entry.name, type: entry.type } as Rider)}
                </p>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-semibold text-primary-900 text-base whitespace-nowrap">
                {entry.events} events
              </p>
              <p className="text-sm text-primary-600 whitespace-nowrap">
                {entry.hours.toFixed(1)} hrs
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Merge fresh RSVP data into the events store.
 * 
 * Builds a Map from the RSVP response, then maps over existing events:
 * - Matching events get event_expectedAttendance and event_status overwritten
 * - Non-matching events retain their stable values (Req 5.2)
 * - Orphan RSVP entries (no matching stable event) are discarded (Req 5.3)
 * 
 * Requirements: 5.1, 5.2, 5.3
 */
function mergeRsvpIntoEvents(
  rsvpEvents: Array<{ event_id: string; event_expectedAttendance: any; event_status: string }>
): void {
  const rsvpMap = new Map(rsvpEvents.map(e => [e.event_id, e]));
  useEventsStore.setState(state => ({
    events: state.events.map(event => {
      const rsvp = rsvpMap.get(event.event_id);
      if (!rsvp) return event; // Req 5.2: retain stable values
      return {
        ...event,
        event_expectedAttendance: rsvp.event_expectedAttendance,
        event_status: rsvp.event_status as ApiEvent['event_status'],
      };
    })
  }));
}

export default function Dashboard() {
  const navigate = useNavigate();
  
  // Auth state
  const { user, isAdmin, isCoach } = useAuth();
  
  // Events state
  const { 
    events, 
  } = useEventsStore();
  
  // Local state for leaderboard
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  
  // Local state for event types (for colors)
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  
  // Local state for user's associated riders with stats
  const [userRidersWithStats, setUserRidersWithStats] = useState<any[]>([]);
  
  // Rider Info state (for coaches/admins)
  const [allRiders, setAllRiders] = useState<Rider[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedRiderId, setSelectedRiderId] = useState<string>('');
  const [selectedRiderInfo, setSelectedRiderInfo] = useState<any>(null);
  const [selectedRiderStats, setSelectedRiderStats] = useState<any>(null);
  const [loadingRiderInfo, setLoadingRiderInfo] = useState(false);

  // Group Info state (for coaches/admins) — independent of the Rider selector.
  // selectedGroup: '' = none selected, else a group name.
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [groups, setGroups] = useState<Group[]>([]);

  // Members of the currently selected group (derived, memoized). Reuses the
  // already-loaded rider list; no additional network call on selection.
  const groupMembers = useMemo(() => membersOf(allRiders, selectedGroup), [allRiders, selectedGroup]);
  
  // Rider data cache (preloaded in background)
  const [riderDataCache, setRiderDataCache] = useState<Map<string, { info: Rider; stats: any }>>(new Map());
  const [cacheTimestamp, setCacheTimestamp] = useState<number>(0);
  const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
  
  // Permissions from dashboard API
  const [apiPermissions, setApiPermissions] = useState<{ show_coach_features?: boolean; show_admin_features?: boolean }>({});
  
  // Upcoming birthdays
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<any[]>([]);
  
  // Events cache for race results enrichment
  const [eventsCache, setEventsCache] = useState<Map<string, ApiEvent>>(new Map());
  
  // Race events from dashboard Lambda response (for RaceRegistrationStatus)
  const [raceEvents, setRaceEvents] = useState<Array<{ event_id: string; event_name: string; event_starttime: string }>>([]);
  
  // Display events from Lambda response (server-side filtered: today or next day with events)
  // Requirements: 5.3, 5.4, 9.3, 9.5
  const [displayEvents, setDisplayEvents] = useState<ApiEvent[]>([]);
  
  // Lazy Rider Info loading state (Requirements: 10.3, 10.4, 10.5)
  const [riderInfoLoaded, setRiderInfoLoaded] = useState(false);
  const [riderInfoLoading, setRiderInfoLoading] = useState(false);
  const riderInfoLoadTimestamp = useRef<number>(0);
  
  // Local loading state (decoupled from eventsStore)
  const [localLoading, setLocalLoading] = useState(true);
  
  // Fetch error state for error UI
  const [fetchError, setFetchError] = useState<string | null>(null);
  
  // Cancel event modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [eventToCancel, setEventToCancel] = useState<{ id: string; name: string } | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellingEvent, setCancellingEvent] = useState(false);
  
  // RSVP update state
  const [updatingRSVP, setUpdatingRSVP] = useState<string | null>(null);
  
  // Compute permissions - use API permissions if available, fallback to auth
  const showCoachFeatures = apiPermissions.show_coach_features ?? isCoach;
  const showAdminFeatures = apiPermissions.show_admin_features ?? isAdmin;

  // For regular users and coaches, filter displayEvents to only events where at least one
  // of their riders is invited. If none of the display events qualify, fall back
  // to the next future event (from all upcoming events) that invites a rider.
  // Admins see all display events unfiltered.
  //
  // Post-Phase-4 (Issue A): the backend now applies the invited-rider filter
  // to non-admin users server-side (see lambda-functions/dashboard-get.py,
  // `if not is_admin:` guard). V.2a (audit-v2a.md) proved the two predicates
  // agree for every non-empty `user_rider_ids`, and the zero-riders short-
  // circuit below compensates the narrow divergence on the empty-user-riders
  // edge. `effectiveDisplayEvents` is therefore an identity pass-through —
  // the backend-filtered `displayEvents` is already the correct set.
  const effectiveDisplayEvents = useMemo(() => displayEvents, [displayEvents]);

  // Rider Skills card state
  const [skillsRider, setSkillsRider] = useState<Rider | null>(null);
  const [skillsRefreshTrigger, setSkillsRefreshTrigger] = useState(0);

  const coachGroup = useMemo(() => {
    if (!userRidersWithStats || userRidersWithStats.length === 0) return null;
    // Find the user's own rider record (role === 'Self') and use its group
    const selfRider = userRidersWithStats.find((r: any) => r.role === 'Self');
    if (selfRider?.group) return selfRider.group;
    // Fallback: use the first Coach-type rider's group
    const coachRider = userRidersWithStats.find((r: any) => r.type === 'Coach');
    if (coachRider?.group) return coachRider.group;
    return null;
  }, [userRidersWithStats]);

  const handleSkillsRiderSelect = (riderId: string) => {
    if (!riderId) {
      setSkillsRider(null);
      return;
    }
    const rider = allRiders.find(r => r.rider_id === riderId);
    if (rider) setSkillsRider(rider);
  };

  /**
   * Get RSVP status for a rider using shared utility
   */
  const getRsvpStatus = useCallback((riderId: string, event: ApiEvent): RSVPDisplayStatus => {
    const attendance = parseExpectedAttendance(event.event_expectedAttendance);
    return getRSVPStatus(attendance, riderId);
  }, []);

  /**
   * Populate dashboard state from a data object (used for both fresh and stale data)
   */
  const populateDashboardState = useCallback((data: any) => {
    if (data.permissions) {
      setApiPermissions(data.permissions);
    }
    if (data.upcoming_events && Array.isArray(data.upcoming_events)) {
      useEventsStore.setState({ events: data.upcoming_events, loading: false });
    }
    if (data.event_types && Array.isArray(data.event_types)) {
      setEventTypes(data.event_types);
    }
    if (data.leaderboard) {
      const students = data.leaderboard.students || [];
      const coaches = data.leaderboard.coaches || [];
      setLeaderboard([...students, ...coaches]);
    }
    if (data.my_riders && Array.isArray(data.my_riders.riders)) {
      setUserRidersWithStats(data.my_riders.riders);
      // Update global auth store so Navigation and ProtectedRoute can check rider status
      useAuthStore.getState().setHasRiders(data.my_riders.riders.length > 0);
    }
    if (data.upcoming_birthdays && Array.isArray(data.upcoming_birthdays)) {
      setUpcomingBirthdays(data.upcoming_birthdays);
    }
    // Backward compat: if initial response still includes all_riders/all_users, use them
    if (data.all_riders && Array.isArray(data.all_riders)) {
      setAllRiders(data.all_riders);
      setRiderInfoLoaded(true);
    }
    if (data.all_users && Array.isArray(data.all_users)) {
      setAllUsers(data.all_users);
    }
    // Build eventsCache solely from upcoming_events — no separate API call (Req 3.1, 3.2)
    // Also include race_events so formatRaceResult can resolve event names for past races
    const cache = new Map<string, ApiEvent>();
    (data.upcoming_events || []).forEach((event: ApiEvent) => {
      cache.set(event.event_id, event);
    });
    // Add race events to cache for race result enrichment (event_name, event_starttime)
    (data.race_events || []).forEach((event: any) => {
      if (event.event_id && !cache.has(event.event_id)) {
        cache.set(event.event_id, event as ApiEvent);
      }
    });
    setEventsCache(cache);
    // Backend (dashboard-get.py) ships display_events in every response since
    // dashboard cache v5 — V.6 CloudWatch audit confirmed zero responses missing
    // the field in the last 30 days. Consume as-is; fall back to empty array if
    // the backend ever regresses so the dashboard shows an empty Upcoming Events
    // card rather than a client-computed alternative set.
    setDisplayEvents(Array.isArray(data.display_events) ? data.display_events : []);
    // Set race events from dashboard response (Requirements: 14.1, 14.3)
    if (data.race_events && Array.isArray(data.race_events)) {
      setRaceEvents(data.race_events);
    }
  }, []);

  /**
   * Fetch dashboard data using split-fetch strategy:
   * - Stable data from getDashboard() (30-min cache or API)
   * - RSVP data always fresh from getDashboardRsvp()
   * Both requests fire concurrently via Promise.allSettled().
   * Requirements: 1.1, 1.2, 1.3, 2.1, 2.2, 2.3, 3.1, 3.2, 3.3, 4.1, 4.2, 4.3, 5.3, 7.1, 7.2
   */
  const fetchDashboardData = useCallback(async () => {
    setLocalLoading(true);
    setFetchError(null);

    const [stableResult, rsvpResult] = await Promise.allSettled([
      restClient.getDashboard(),
      restClient.getDashboardRsvp(),
    ]);

    // Handle stable data result
    if (stableResult.status === 'fulfilled') {
      populateDashboardState(stableResult.value);
    } else {
      // Stable fetch failed — try stale cache fallback (Req 7.1)
      const staleData = restClient.getStaleDashboardCache();
      if (staleData) {
        populateDashboardState(staleData);
      }
      const message = stableResult.reason instanceof Error
        ? stableResult.reason.message
        : 'Failed to load dashboard';
      setFetchError(message);
      console.error('Failed to load dashboard:', stableResult.reason);
    }

    // Handle RSVP data result (Req 7.2: graceful degradation on failure)
    if (rsvpResult.status === 'fulfilled') {
      mergeRsvpIntoEvents(rsvpResult.value.rsvp_events);
      // Also merge fresh RSVP data into displayEvents so RSVP icons show current status
      const rsvpMap = new Map(rsvpResult.value.rsvp_events.map((e: any) => [e.event_id, e]));
      setDisplayEvents(prev => prev.map(event => {
        const rsvp = rsvpMap.get(event.event_id);
        if (!rsvp) return event;
        return {
          ...event,
          event_expectedAttendance: rsvp.event_expectedAttendance,
          event_status: rsvp.event_status as ApiEvent['event_status'],
        };
      }));
    }
    // On RSVP failure: leave RSVP fields from stable data as-is

    // Clear loading only after both promises settle (Req 4.3)
    setLocalLoading(false);
  }, [populateDashboardState]);

  /**
   * Fetch all dashboard data on mount
   * Requirements: 2.1, 4.1, 5.1
   */
  useEffect(() => {
    fetchDashboardData();
  }, []);

  /**
   * Lazy load rider info data for coaches/admins (Requirements: 10.3, 10.4, 10.5)
   * Triggered by Intersection Observer when Rider Info card scrolls into view,
   * or on first dropdown interaction.
   */
  const loadRiderInfo = useCallback(async () => {
    if (riderInfoLoaded || riderInfoLoading) return;
    // Check if cached data is still valid (1-hour TTL)
    const now = Date.now();
    if (riderInfoLoadTimestamp.current > 0 && (now - riderInfoLoadTimestamp.current) < CACHE_DURATION) {
      return;
    }
    setRiderInfoLoading(true);
    try {
      const data = await restClient.getDashboardSection('rider_info');
      if (data.all_riders && Array.isArray(data.all_riders)) {
        setAllRiders(data.all_riders);
      }
      if (data.all_users && Array.isArray(data.all_users)) {
        setAllUsers(data.all_users);
      }
      setRiderInfoLoaded(true);
      riderInfoLoadTimestamp.current = Date.now();

      // Load groups for the Group selector. Fail-safe and independent: a groups
      // failure must not break rider info or the rest of the dashboard.
      try {
        const groupsData = await restClient.getGroups();
        const sortedGroups = [...groupsData].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        setGroups(sortedGroups);
      } catch (groupsError) {
        console.error('[Dashboard] Failed to load groups:', groupsError);
      }
    } catch (error) {
      console.error('[Dashboard] Failed to lazy-load rider info:', error);
    } finally {
      setRiderInfoLoading(false);
    }
  }, [riderInfoLoaded, riderInfoLoading]);

  /**
   * Auto-load rider info in background after main dashboard data loads (for coaches/admins).
   * Fires once after localLoading clears, without blocking the dashboard render.
   */
  useEffect(() => {
    if (!showCoachFeatures || riderInfoLoaded || riderInfoLoading || localLoading) return;
    loadRiderInfo();
  }, [showCoachFeatures, riderInfoLoaded, riderInfoLoading, localLoading, loadRiderInfo]);


  
  /**
   * Preload rider data in background after dashboard loads (for coaches/admins)
   * This runs asynchronously and doesn't block the initial page render
   */
  useEffect(() => {
    // Only preload if user has coach/admin features and riders are available
    if (!showCoachFeatures || allRiders.length === 0) return;
    
    // Check if cache is still valid (less than 1 hour old)
    const now = Date.now();
    if (cacheTimestamp > 0 && (now - cacheTimestamp) < CACHE_DURATION) {
      return; // Cache is still valid, no need to reload
    }
    
    // Preload rider data in background
    preloadRiderData();
  }, [allRiders, showCoachFeatures]);

  /**
   * Get color for event type badge
   */
  const getEventTypeColor = useCallback((eventTypeName: string): string => {
    const eventType = eventTypes.find(et => et.name === eventTypeName);
    return eventType?.color || '#435d7d'; // Default to primary blue
  }, [eventTypes]);
  
  /**
   * Preload all rider data in background (for coaches/admins)
   * This fetches stats for all riders and caches them for 1 hour
   * Runs asynchronously without blocking the UI
   */
  const preloadRiderData = useCallback(async () => {
    if (allRiders.length === 0) return;
    
    const currentSeason = new Date().getFullYear().toString();
    const newCache = new Map<string, { info: Rider; stats: any }>();
    
    // Fetch data for all riders in parallel (but limit concurrency to avoid overwhelming the API)
    const batchSize = 5; // Process 5 riders at a time
    for (let i = 0; i < allRiders.length; i += batchSize) {
      const batch = allRiders.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(async (rider) => {
          try {
            // Fetch stats
            const stats = await restClient.getRiderStats(rider.rider_id, currentSeason);
            
            // Fetch time trials
            try {
              const timeTrials = await restClient.getTimeTrials({ rider_id: rider.rider_id });
              stats.time_trials = timeTrials;
            } catch {
              stats.time_trials = [];
            }
            
            // Fetch race results
            try {
              const raceResults = await restClient.getRaceResults({ rider_id: rider.rider_id });
              // Enrich race results with event names from raceEvents and eventsCache
              stats.race_results = raceResults.map((r: any) => {
                if (r.event_name) return r;
                const cachedEvent = eventsCache.get(r.event_id);
                if (cachedEvent?.event_name) return { ...r, event_name: cachedEvent.event_name };
                const raceEvent = raceEvents.find((re: any) => re.event_id === r.event_id);
                if (raceEvent?.event_name) return { ...r, event_name: raceEvent.event_name };
                return r;
              });
            } catch {
              stats.race_results = [];
            }
            
            // Store in cache
            newCache.set(rider.rider_id, {
              info: rider,
              stats: stats
            });
          } catch (error) {
            // If fetch fails for a rider, store minimal data
            newCache.set(rider.rider_id, {
              info: rider,
              stats: { total_hours: 0, event_count: 0, time_trials: [], race_results: [] }
            });
          }
        })
      );
    }
    
    // Update cache and timestamp
    setRiderDataCache(newCache);
    setCacheTimestamp(Date.now());
  }, [allRiders, eventsCache, raceEvents]);

  /**
   * User is automatically loaded by AuthProvider, no need to fetch
   */

  // upcomingEvents useMemo removed — now using displayEvents state from Lambda response (Req 5.3, 5.4)

  /**
   * Handle rider selection in Rider Info dropdown
   * Now uses cached data if available, otherwise fetches on-demand
   */
  const handleRiderSelect = useCallback(async (riderId: string) => {
    setSelectedRiderId(riderId);
    
    if (!riderId) {
      setSelectedRiderInfo(null);
      setSelectedRiderStats(null);
      return;
    }
    
    // Check if data is in cache
    const cachedData = riderDataCache.get(riderId);
    if (cachedData) {
      // Use cached data - instant display!
      setSelectedRiderInfo(cachedData.info);
      setSelectedRiderStats(cachedData.stats);
      return;
    }
    
    // Cache miss - fetch on-demand (fallback for edge cases)
    setLoadingRiderInfo(true);
    
    try {
      const rider = allRiders.find(r => r.rider_id === riderId);
      if (!rider) {
        setSelectedRiderInfo(null);
        setSelectedRiderStats(null);
        return;
      }
      
      setSelectedRiderInfo(rider);
      
      const currentSeason = new Date().getFullYear().toString();
      const stats = await restClient.getRiderStats(riderId, currentSeason);
      
      // Fetch ALL time trials for this rider (no season filter)
      try {
        const timeTrials = await restClient.getTimeTrials({ rider_id: riderId });
        stats.time_trials = timeTrials;
      } catch (error) {
        // If time trials fetch fails, continue without them
        stats.time_trials = [];
      }
      
      // Fetch race results for this rider
      try {
        const raceResults = await restClient.getRaceResults({ rider_id: riderId });
        // Enrich race results with event names from raceEvents and eventsCache
        stats.race_results = raceResults.map((r: any) => {
          if (r.event_name) return r;
          const cachedEvent = eventsCache.get(r.event_id);
          if (cachedEvent?.event_name) return { ...r, event_name: cachedEvent.event_name };
          const raceEvent = raceEvents.find(re => re.event_id === r.event_id);
          if (raceEvent?.event_name) return { ...r, event_name: raceEvent.event_name };
          return r;
        });
      } catch (error) {
        // If race results fetch fails, continue without them
        stats.race_results = [];
      }
      
      setSelectedRiderStats(stats);
      
      // Update cache with newly fetched data
      setRiderDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(riderId, { info: rider, stats });
        return newCache;
      });
    } catch (error) {
      setSelectedRiderStats({ total_hours: 0, event_count: 0, time_trials: [], race_results: [] });
    } finally {
      setLoadingRiderInfo(false);
    }
  }, [allRiders, riderDataCache, eventsCache, raceEvents]);

  /**
   * Format date for display
   */
  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  /**
   * Format time for display
   */
  const formatTime = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }, []);
  
  /**
   * Format race result for display
   */
  const formatRaceResult = useCallback((result: any) => {
    const event = eventsCache.get(result.event_id);
    const eventName = event?.event_name || result.event_name || 'Unknown Event';
    
    // Format date as YYYY-MM-DD
    // Priority: result.event_starttime (enriched by API) > event cache > result.date > result.timestamp
    let eventDate = 'Unknown Date';
    if (result.event_starttime) {
      const date = new Date(result.event_starttime);
      eventDate = date.toISOString().split('T')[0];
    } else if (event?.event_starttime) {
      const date = new Date(event.event_starttime);
      eventDate = date.toISOString().split('T')[0];
    } else if (result.date) {
      eventDate = result.date;
    } else if (result.timestamp) {
      const ts = Number(result.timestamp);
      const date = new Date(ts > 1e12 ? ts : ts * 1000);
      eventDate = date.toISOString().split('T')[0];
    }
    
    const position = formatPosition(result.position, result.total_racers, result.dnf);
    const time = result.total_time ? formatRaceTime(result.total_time) : '';
    
    return {
      eventName,
      eventDate,
      position,
      time,
    };
  }, [eventsCache]);

  /**
   * Open cancel event modal
   */
  const openCancelModal = useCallback((eventId: string, eventName: string) => {
    setEventToCancel({ id: eventId, name: eventName });
    setCancellationReason('');
    setShowCancelModal(true);
  }, []);

  /**
   * Close cancel event modal
   */
  const closeCancelModal = useCallback(() => {
    setShowCancelModal(false);
    setEventToCancel(null);
    setCancellationReason('');
  }, []);

  /**
   * Handle cancel event submission
   */
  const handleCancelEvent = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!eventToCancel) return;
    
    setCancellingEvent(true);
    
    try {
      await restClient.updateEvent(eventToCancel.id, {
        event_id: eventToCancel.id,
        event_status: 'canceled'
      });
      
      // Notify in the background, but surface the outcome instead of discarding
      // it. (FR-9)
      const cancelledName = eventToCancel.name;
      restClient.sendCancelNotification(
        eventToCancel.id,
        cancellationReason.trim() || undefined
      )
        .then((result) => {
          const outcome = describeNotificationOutcome(result);
          if (outcome.message) {
            alert(outcome.message);
          }
        })
        .catch(() => {
          alert(describeNotificationFailure(cancelledName));
        });
      
      await fetchDashboardData();
      
      alert(`Event "${eventToCancel.name}" has been canceled successfully.\n\nEmail and GroupMe notifications are being sent in the background to all users.`);
      
      closeCancelModal();
    } catch (error) {
      alert('Error canceling event. Please try again.');
    } finally {
      setCancellingEvent(false);
    }
  }, [eventToCancel, cancellationReason, fetchDashboardData, closeCancelModal]);

  /**
   * Handle RSVP status update for a rider on an event using shared utility
   */
  const handleRSVPUpdate = useCallback(async (event: ApiEvent, riderId: string, currentStatus: RSVPDisplayStatus) => {
    const updateKey = `${event.event_id}-${riderId}`;
    
    setUpdatingRSVP(updateKey);
    
    try {
      const result = await updateRSVPStatus(event, riderId, currentStatus);
      
      if (result.success) {
        useEventsStore.setState(state => ({
          events: state.events.map(e => 
            e.event_id === event.event_id 
              ? { ...e, event_expectedAttendance: result.updatedAttendance }
              : e
          )
        }));
        // Also update displayEvents so the RSVP icon updates immediately
        setDisplayEvents(prev => prev.map(e =>
          e.event_id === event.event_id
            ? { ...e, event_expectedAttendance: result.updatedAttendance }
            : e
        ));
        // Invalidate dashboard cache so next load fetches fresh RSVP data
        // Requirements: 6.1, 6.4
        restClient.clearDashboardCache();
      } else {
        alert('Failed to update RSVP. Please try again.');
        await fetchDashboardData();
      }
    } catch (err) {
      alert('Failed to update RSVP. Please try again.');
      await fetchDashboardData();
    } finally {
      setUpdatingRSVP(null);
    }
  }, [fetchDashboardData]);

  const isLoading = localLoading;
  const hasNoRiders = !showAdminFeatures && userRidersWithStats.length === 0;
  
  // Split leaderboard into students and coaches for rendering
  const leaderboardStudents = useMemo(() => 
    leaderboard.filter(entry => entry.type !== 'Coach'),
    [leaderboard]
  );
  
  const leaderboardCoaches = useMemo(() => 
    leaderboard.filter(entry => entry.type === 'Coach'),
    [leaderboard]
  );

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-primary-900">Dashboard</h1>
          <p className="text-primary-600 mt-1">
            {user?.given_name || user?.name || 'User'} ({user?.email})
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-2 md:px-4 pt-3 pb-8">
        {isLoading && (
          <div className="flex justify-center items-center py-12" aria-live="polite">
            <LoadingSpinner size="lg" />
            <span className="sr-only">Loading dashboard data...</span>
          </div>
        )}

        {/* Error state with no stale data: show error message + retry button */}
        {!isLoading && fetchError && events.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center" role="alert">
            <svg
              className="w-16 h-16 mb-4 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <p className="text-lg text-primary-900 font-semibold mb-2">Failed to load dashboard</p>
            <p className="text-primary-600 mb-4">{fetchError}</p>
            <Button
              onClick={() => fetchDashboardData()}
              className="text-base px-6 py-3 min-h-[48px]"
              aria-label="Retry loading dashboard"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Stale data warning banner */}
        {!isLoading && fetchError && events.length > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-center justify-between" role="alert">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-yellow-800 text-sm font-medium">Data may be outdated</span>
            </div>
            <Button
              variant="secondary"
              onClick={() => fetchDashboardData()}
              className="text-sm px-3 py-1 min-h-[36px]"
              aria-label="Retry loading dashboard"
            >
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !fetchError && hasNoRiders && (
          <div className="text-center py-12" role="status" data-testid="no-rider-guard">
            <svg 
              className="w-16 h-16 mx-auto mb-4 text-primary-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
              />
            </svg>
            <p className="text-lg text-primary-900 font-semibold mb-2">
              No rider associated for this season
            </p>
            <p className="text-primary-600">
              Contact your team administrator to get associated with a rider.
            </p>
          </div>
        )}

        {!isLoading && !hasNoRiders && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Events */}
            <section className="lg:col-span-2" aria-labelledby="upcoming-events-heading">
              <Card>
                <div className="mb-4">
                  <h2 id="upcoming-events-heading" className="text-2xl font-semibold text-primary-900 mb-4">
                    Upcoming Events
                  </h2>
                </div>

                {effectiveDisplayEvents.length === 0 ? (
                  <div className="text-center py-8 text-primary-600" role="status">
                    <svg 
                      className="w-16 h-16 mx-auto mb-4 text-primary-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                      />
                    </svg>
                    <p>No upcoming events scheduled</p>
                  </div>
                ) : (
                  <ul className="space-y-3" role="list">
                    {effectiveDisplayEvents.map((event: ApiEvent) => {
                      const isCanceled = isEventCanceled(event); // null/undefined treated as active
                      
                      // Check if event is currently happening (between start and end time)
                      const now = new Date();
                      const eventStart = new Date(event.event_starttime);
                      const eventEnd = new Date(event.event_endtime);
                      const isHappeningNow = now >= eventStart && now <= eventEnd;
                      
                      return (
                        <li key={event.event_id}>
                          <div className={`p-3 sm:p-4 rounded-lg ${
                            isCanceled 
                              ? 'bg-red-50' 
                              : isHappeningNow 
                                ? 'bg-green-100 ring-2 ring-green-400' 
                                : 'bg-primary-50'
                          }`}>
                            <button
                              className="w-full text-left hover:opacity-80 transition-opacity"
                              onClick={() => navigate(`/events/${event.event_id}`)}
                              data-testid="event-item"
                              aria-label={`View details for ${event.event_name} on ${formatDate(event.event_starttime)} at ${formatTime(event.event_starttime)}`}
                            >
                              <div className="space-y-3">
                                {/* Event Name and Type */}
                                <div className="flex items-start justify-between gap-3">
                                  <h3 className={`font-semibold text-primary-900 flex-1 text-lg ${isCanceled ? 'line-through' : ''}`}>
                                    {event.event_name}
                                    {isCanceled && (
                                      <span className="ml-2 text-red-600 font-bold text-base no-underline" style={{ textDecoration: 'none' }}>
                                        CANCELLED
                                      </span>
                                    )}
                                  </h3>
                                  <span 
                                    className="inline-block px-3 py-1 text-base font-semibold text-white rounded-full whitespace-nowrap"
                                    style={{ backgroundColor: getEventTypeColor(event.event_type) }}
                                  >
                                    {event.event_type}
                                  </span>
                                </div>
                                
                                {/* Date, Time, Location - each on separate line */}
                                <div className={`text-lg text-primary-600 space-y-2 ${isCanceled ? 'line-through' : ''}`}>
                                  <div>
                                    <strong>Date:</strong> {formatDate(event.event_starttime)}
                                  </div>
                                  <div>
                                    <strong>Time:</strong> {formatTime(event.event_starttime)} - {formatTime(event.event_endtime)}
                                  </div>
                                  <div>
                                    <strong>Location:</strong> {event.event_location_name || event.event_location}
                                  </div>
                                </div>
                                
                                {/* RSVP Status for user's associated riders */}
                                {!isCanceled && userRidersWithStats.length > 0 && (() => {
                                  // Check if event is in the future
                                  const now = new Date();
                                  const eventStart = new Date(event.event_starttime);
                                  const isFutureEvent = eventStart > now;
                                  
                                  // Only show RSVP status for future events
                                  if (!isFutureEvent) return null;

                                  // Filter riders to only those invited to this event
                                  const invitedRiders = filterRidersByEvent([...userRidersWithStats], event)
                                    .sort((a, b) => formatRiderName(a).localeCompare(formatRiderName(b)));
                                  
                                  // Don't show RSVP section if none of the user's riders are invited
                                  if (invitedRiders.length === 0) return null;
                                  
                                  return (
                                    <div className="mt-3 pt-3 border-t border-primary-200">
                                      <div className="text-base font-semibold text-primary-900 mb-2">RSVP Status:</div>
                                      <div className="space-y-2">
                                        {invitedRiders
                                          .map(rider => {
                                          const rsvpStatus = getRsvpStatus(rider.rider_id, event);
                                          const updateKey = `${event.event_id}-${rider.rider_id}`;
                                          const isUpdating = updatingRSVP === updateKey;
                                          
                                          const getRSVPIcon = (status: RSVPDisplayStatus) => {
                                            switch (status) {
                                              case 'coming':
                                                return (
                                                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                  </svg>
                                                );
                                              case 'maybe':
                                                return (
                                                  <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                                  </svg>
                                                );
                                              case 'not-coming':
                                                return (
                                                  <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                  </svg>
                                                );
                                              default:
                                                return (
                                                  <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                                  </svg>
                                                );
                                            }
                                          };
                                          
                                          const getRSVPLabel = (status: RSVPDisplayStatus) => {
                                            switch (status) {
                                              case 'coming': return 'Yes';
                                              case 'maybe': return 'Maybe';
                                              case 'not-coming': return 'No';
                                              default: return 'RSVP';
                                            }
                                          };
                                          
                                          const rsvpButtonStyles = (() => {
                                            switch (rsvpStatus) {
                                              case 'coming':
                                                return 'bg-green-50 border-2 border-green-500 text-green-700 hover:bg-green-100 shadow-sm';
                                              case 'maybe':
                                                return 'bg-orange-50 border-2 border-orange-400 text-orange-700 hover:bg-orange-100 shadow-sm';
                                              case 'not-coming':
                                                return 'bg-red-50 border-2 border-red-400 text-red-700 hover:bg-red-100 shadow-sm';
                                              default:
                                                return 'bg-primary-500 border-2 border-primary-500 text-white hover:bg-primary-600 shadow-lg ring-2 ring-primary-300 ring-offset-1 animate-pulse';
                                            }
                                          })();
                                          
                                          return (
                                            <div className="flex items-center justify-between gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                                              <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900">{formatRiderName(rider)}</p>
                                                <p className="text-sm text-gray-600">{rider.group}</p>
                                              </div>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRSVPUpdate(event, rider.rider_id, rsvpStatus);
                                                }}
                                                className={`flex flex-col items-center gap-1.5 w-[100px] h-[70px] justify-center flex-shrink-0 rounded-lg font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${rsvpButtonStyles}`}
                                                title={getRSVPLabel(rsvpStatus)}
                                                disabled={isUpdating}
                                              >
                                                {isUpdating ? (
                                                  <>
                                                    <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <span className="text-xs text-center leading-tight">Updating...</span>
                                                  </>
                                                ) : (
                                                  <>
                                                    {getRSVPIcon(rsvpStatus)}
                                                    <span className="text-sm font-semibold text-center leading-tight">{getRSVPLabel(rsvpStatus)}</span>
                                                  </>
                                                )}
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </button>
                            
                            {/* Coach Check-In button and Admin cancel button */}
                            {(showCoachFeatures || showAdminFeatures) && !isCanceled && (() => {
                              // Check if Check-In button should be shown
                              // Show from 1 hour before event start until 3 hours after event end
                              // This applies to both coaches AND admins on the Dashboard
                              // (Admins can still access /checkin directly via navigation at any time)
                              const now = new Date();
                              const eventStart = new Date(event.event_starttime);
                              const eventEnd = new Date(event.event_endtime);
                              
                              // Calculate 1 hour before event start
                              const oneHourBeforeStart = new Date(eventStart.getTime() - 60 * 60 * 1000);
                              
                              // Calculate 3 hours after event end
                              const threeHoursAfterEnd = new Date(eventEnd.getTime() + 3 * 60 * 60 * 1000);
                              
                              // Check-in is available from 1 hour before start until 3 hours after end
                              const canCheckIn = now >= oneHourBeforeStart && now <= threeHoursAfterEnd;
                              
                              // Show Check-In button only during valid time window (for both coaches and admins)
                              // Show Cancel button for admins only BEFORE event start time
                              // Show Incident button for coaches/admins AFTER event start time (no end cutoff)
                              const showCheckInButton = (showCoachFeatures || showAdminFeatures) && canCheckIn;
                              const showCancelButton = showAdminFeatures && now < eventStart;
                              const showIncidentButton = (showCoachFeatures || showAdminFeatures) && !isCanceled && now > eventStart;
                              
                              if (!showCheckInButton && !showCancelButton && !showIncidentButton) return null;
                              
                              return (
                                <div className="mt-3 pt-3 border-t border-primary-200 text-center flex gap-3 justify-center">
                                  {showCheckInButton && (
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(checkinPath(event.event_id));
                                      }}
                                      className="text-base px-6 py-3 min-h-[48px]"
                                      style={{ backgroundColor: '#28a745', color: 'white' }}
                                      aria-label={`Check in for ${event.event_name}`}
                                    >
                                      Rider Check-in
                                    </Button>
                                  )}
                                  {showIncidentButton && (
                                    <DashboardIncidentButton
                                      event={event}
                                      isCanceled={isCanceled}
                                      showCoachFeatures={showCoachFeatures}
                                      showAdminFeatures={showAdminFeatures}
                                    />
                                  )}
                                  {showCancelButton && (
                                    <Button
                                      variant="danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCancelModal(event.event_id, event.event_name);
                                      }}
                                      className="text-base px-6 py-3 min-h-[48px]"
                                      aria-label={`Cancel ${event.event_name}`}
                                    >
                                      Cancel
                                    </Button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Birthdays Section */}
                <div className="mt-4 pt-4 border-t border-primary-200">
                  <h3 className="text-xl font-semibold text-primary-900 mb-3">Birthdays</h3>
                  {upcomingBirthdays.length === 0 ? (
                    <p className="text-lg text-primary-600">No upcoming birthdays</p>
                  ) : (
                    <ul className="space-y-2">
                      {upcomingBirthdays.map((birthday, index) => (
                        <li key={index} className="text-lg text-primary-700">
                          {formatRiderName({ name: birthday.name, type: birthday.type } as Rider)} - {birthday.formatted_date}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Card>
            </section>

            {/* Training Plan — current week's plan for the group's level.
                Coaches are locked to their group's level; admins can switch levels. */}
            {showCoachFeatures && (
              <TrainingPlanPanel
                coachGroup={coachGroup}
                isAdmin={showAdminFeatures}
                className="lg:col-span-2"
              />
            )}

            {/* Group Skills Overview (Coaches/Admins Only) */}
            {showCoachFeatures && (
              <section aria-labelledby="rider-skills-heading" className="lg:col-span-2">
                <GroupSkillsCard
                  coachGroup={coachGroup}
                  isAdmin={showAdminFeatures}
                  onRiderSelect={handleSkillsRiderSelect}
                  refreshTrigger={skillsRefreshTrigger}
                />
              </section>
            )}

            {/* Rider Info (Coaches/Admins Only) */}
            {showCoachFeatures && (
              <section aria-labelledby="rider-info-heading" className="lg:col-span-2">
                <Card>
                  <div className="mb-4">
                    <h2 id="rider-info-heading" className="text-2xl font-semibold text-primary-900 mb-4">
                      Rider and Group Info
                    </h2>

                    {/* Group selector (independent of the Rider selector). Selecting a
                        group shows its members; it does not filter the Rider dropdown. */}
                    {riderInfoLoaded && (
                      <div className="mb-4">
                        <select
                          value={selectedGroup}
                          onChange={(e) => setSelectedGroup(e.target.value)}
                          className="w-full px-3 py-2 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          aria-label="Select a group to view its members"
                          data-testid="dashboard-group-select"
                        >
                          <option value="">Select a group...</option>
                          {groups.map((group) => (
                            <option key={group.group_id} value={group.name}>
                              {group.name}
                            </option>
                          ))}
                        </select>

                        {selectedGroup && (
                          <div className="mt-3 p-3 bg-primary-50 rounded-lg" data-testid="dashboard-group-members">
                            <h3 className="font-semibold text-primary-900 mb-2">
                              {selectedGroup}
                              {' \u2014 '}
                              {groupMembers.length} rider{groupMembers.length === 1 ? '' : 's'}
                            </h3>
                            {groupMembers.length === 0 ? (
                              <p className="text-primary-600 text-sm">No riders in this group</p>
                            ) : (
                              <ul className="space-y-1">
                                {groupMembers.map((rider) => (
                                  <li
                                    key={rider.rider_id}
                                    className="text-primary-800"
                                    data-testid="group-member-item"
                                  >
                                    {formatRiderName(rider)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {riderInfoLoading && !riderInfoLoaded && (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner size="md" />
                        <span className="ml-3 text-primary-600">Loading rider data...</span>
                      </div>
                    )}
                    
                    {riderInfoLoaded && allRiders.length > 0 && (
                    <select
                      value={selectedRiderId}
                      onChange={(e) => {
                        handleRiderSelect(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-label="Select a rider to view information"
                    >
                      <option value="">Select a rider...</option>
                      {[...allRiders]
                        .sort((a, b) => {
                          const aIsCoach = a.type === 'Coach' ? 1 : 0;
                          const bIsCoach = b.type === 'Coach' ? 1 : 0;
                          if (aIsCoach !== bIsCoach) return aIsCoach - bIsCoach;
                          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
                        })
                        .map(rider => (
                        <option key={rider.rider_id} value={rider.rider_id}>
                          {formatRiderName(rider)}
                        </option>
                      ))}
                    </select>
                    )}
                  </div>

                  {loadingRiderInfo && (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner size="md" />
                      <span className="ml-3 text-primary-600">Loading rider information...</span>
                    </div>
                  )}

                  {!loadingRiderInfo && selectedRiderInfo && selectedRiderStats && (
                    <div className="space-y-4">
                      {/* Rider Details */}
                      <div className="p-3 sm:p-4 bg-primary-50 rounded-lg">
                        <div className="space-y-1">
                          <h3 className="font-semibold text-primary-900 text-xl">
                            {formatRiderName(selectedRiderInfo)}
                          </h3>
                          <p className="text-base text-primary-600">
                            Attendance: {selectedRiderStats.event_count} Events | {selectedRiderStats.total_hours} Hrs
                          </p>
                          <div className="border-t border-primary-200 mt-2 pt-2"></div>
                          
                          {selectedRiderInfo.type === 'Coach' || selectedRiderInfo.category === 'Coach' ? (
                            <>
                              <p className="text-base text-primary-600">
                                {selectedRiderInfo.level ? `Coach Level ${selectedRiderInfo.level}` : 'Coach Level MISSING'}
                              </p>
                              <p className="text-base text-primary-600">
                                Group: {selectedRiderInfo.group || 'N/A'}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-base text-primary-600">
                                Category: {selectedRiderInfo.category || 'N/A'}
                              </p>
                              <p className="text-base text-primary-600">
                                Group: {selectedRiderInfo.group || 'N/A'}
                              </p>
                              {selectedRiderInfo.grade && (
                                <p className="text-base text-primary-600">
                                  Grade: {selectedRiderInfo.grade}
                                </p>
                              )}
                            </>
                          )}
                          
                          {/* Race Registration Status */}
                          {selectedRiderInfo.type !== 'Coach' && selectedRiderInfo.category !== 'Coach' && raceEvents.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-primary-200">
                              <RaceRegistrationStatus
                                raceRegistrations={selectedRiderInfo.race_registrations || {}}
                                raceEvents={raceEvents}
                                className="text-base text-primary-600"
                              />
                            </div>
                          )}
                          
                          {/* Time Trials */}
                          {selectedRiderStats.time_trials && selectedRiderStats.time_trials.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-primary-200">
                              <p className="text-base font-semibold text-primary-900 mb-1">Time-Trials:</p>
                              {selectedRiderStats.time_trials
                                .sort((a: any, b: any) => {
                                  // Sort by date (newest first)
                                  return new Date(b.date).getTime() - new Date(a.date).getTime();
                                })
                                .slice(0, 5)
                                .map((trial: any, index: number) => {
                                // Format time from seconds to MM:SS
                                const formatTime = (seconds: number) => {
                                  const mins = Math.floor(seconds / 60);
                                  const secs = seconds % 60;
                                  return `${mins}:${secs.toString().padStart(2, '0')}`;
                                };
                                
                                // Extract date in YYYY-MM-DD format (avoid timezone issues)
                                const formatDate = (dateString: string) => {
                                  // If already in YYYY-MM-DD format, return as-is
                                  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                                    return dateString;
                                  }
                                  // Otherwise extract the date part from ISO timestamp
                                  return dateString.split('T')[0];
                                };
                                
                                return (
                                  <p key={index} className="text-base text-primary-600">
                                    {formatDate(trial.date)} | {formatTime(trial.time)} | {trial.trail_name || 'N/A'}
                                  </p>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Race Results */}
                          {selectedRiderStats.race_results && selectedRiderStats.race_results.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-primary-200">
                              <p className="text-base font-semibold text-primary-900 mb-1">Race Results:</p>
                              {selectedRiderStats.race_results
                                .map((result: any) => ({
                                  result,
                                  formatted: formatRaceResult(result),
                                }))
                                .sort((a: { formatted: { eventDate: string } }, b: { formatted: { eventDate: string } }) => b.formatted.eventDate.localeCompare(a.formatted.eventDate))
                                .slice(0, 5)
                                .map(({ formatted }: { formatted: { eventDate: string; eventName: string; position: string } }, index: number) => {
                                  return (
                                    <p key={index} className="text-base text-primary-600 truncate">
                                      {formatted.eventDate} | {formatted.position} | {formatted.eventName}
                                    </p>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Associated Users / Contact Information */}
                      <div>
                        <h3 className="text-lg font-semibold text-primary-900 mb-3">Contact Information</h3>
                        {(() => {
                          if (!selectedRiderInfo.associatedUsers) {
                            return <p className="text-primary-600">No associated users</p>;
                          }
                          
                          try {
                            const users = JSON.parse(selectedRiderInfo.associatedUsers);
                            if (!users || users.length === 0) {
                              return <p className="text-primary-600">No associated users</p>;
                            }
                            
                            // Map users to their details and sort by name
                            const usersWithDetails = users.map((user: any) => {
                              const userDetails = allUsers.find(
                                u => u.username === user.user_id || u.email === user.user_id
                              );
                              return {
                                user,
                                userDetails,
                                name: userDetails?.name || userDetails?.given_name || 'N/A'
                              };
                            }).sort((a: any, b: any) => a.name.localeCompare(b.name));
                            
                            return (
                              <div className="space-y-4">
                                {usersWithDetails.map(({ user, userDetails }: any, index: number) => (
                                  <div key={index} className="p-3 sm:p-4 border-l-4 border-primary-500 bg-primary-50 rounded">
                                    <p className="mb-2">
                                      <strong className="text-primary-900">Name:</strong>{' '}
                                      <span className="text-primary-700">{userDetails?.name || userDetails?.given_name || 'N/A'}</span>
                                    </p>
                                    <p className="mb-2">
                                      <strong className="text-primary-900">Relationship:</strong>{' '}
                                      <span className="text-primary-700">{user.role || 'N/A'}</span>
                                    </p>
                                    <p className="mb-2">
                                      <strong className="text-primary-900">Phone:</strong>{' '}
                                      {userDetails?.phone ? (
                                        <a 
                                          href={`tel:${userDetails.phone}`}
                                          className="text-primary-600 hover:text-primary-800 underline"
                                        >
                                          {formatPhoneParenthesized(userDetails.phone)}
                                        </a>
                                      ) : (
                                        <span className="text-primary-700">N/A</span>
                                      )}
                                    </p>
                                    <p className="mb-2">
                                      <strong className="text-primary-900">Text:</strong>{' '}
                                      {userDetails?.phone ? (
                                        <a 
                                          href={`sms:${userDetails.phone}`}
                                          className="text-primary-600 hover:text-primary-800 underline"
                                        >
                                          {formatPhoneParenthesized(userDetails.phone)}
                                        </a>
                                      ) : (
                                        <span className="text-primary-700">N/A</span>
                                      )}
                                    </p>
                                    <p>
                                      <strong className="text-primary-900">Email:</strong>{' '}
                                      {userDetails?.email ? (
                                        <a 
                                          href={`mailto:${userDetails.email}`}
                                          className="text-primary-600 hover:text-primary-800 underline"
                                        >
                                          {userDetails.email}
                                        </a>
                                      ) : (
                                        <span className="text-primary-700">N/A</span>
                                      )}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            );
                          } catch (error) {
                            return <p className="text-danger-600">Error loading user information</p>;
                          }
                        })()}
                      </div>
                    </div>
                  )}
                </Card>
              </section>
            )}

            {/* User's Associated Riders */}
            <section aria-labelledby="my-riders-heading">
              <Card>
                <div className="mb-4">
                  <h2 id="my-riders-heading" className="text-2xl font-semibold text-primary-900">
                    My Riders
                  </h2>
                </div>

                {userRidersWithStats.length === 0 ? (
                  <div className="text-center py-8 text-primary-600" role="status">
                    <svg 
                      className="w-16 h-16 mx-auto mb-4 text-primary-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
                      />
                    </svg>
                    <p>No associated riders</p>
                  </div>
                ) : (
                  <ul className="space-y-3" data-testid="riders-list" role="list">
                    {userRidersWithStats.map(rider => {
                      const isCoach = rider.type === 'Coach' || rider.category === 'Coach';
                      const coachLevel = isCoach ? (rider.level ? `Coach Level ${rider.level}` : 'Coach Level MISSING') : null;
                      
                      return (
                        <li key={rider.rider_id}>
                          <button
                            className="w-full p-3 sm:p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors text-left"
                            onClick={() => navigate(`/riders/${rider.rider_id}`)}
                            data-testid="rider-item"
                            aria-label={`View details for ${formatRiderName(rider)}`}
                          >
                            <div className="space-y-1">
                              <h3 className="font-semibold text-primary-900 text-xl">
                                {formatRiderName(rider)}
                              </h3>
                              <p className="text-base text-primary-600">
                                Attendance: {rider.stats?.event_count || 0} Events | {rider.stats?.total_hours || 0} Hrs
                              </p>
                              <div className="border-t border-primary-200 mt-2 pt-2"></div>
                              {coachLevel ? (
                                <>
                                  {rider.birthday && (
                                    <p className="text-base text-primary-600">
                                      Birthday: {(() => {
                                        const [y, m, d] = rider.birthday.split('-');
                                        return `${m}/${d}/${y}`;
                                      })()}
                                    </p>
                                  )}
                                  <p className="text-base text-primary-600">
                                    {coachLevel}
                                  </p>
                                  <p className="text-base text-primary-600">
                                    Group: {rider.group}
                                  </p>
                                </>
                              ) : (
                                <>
                                  {rider.birthday && (
                                    <p className="text-base text-primary-600">
                                      Birthday: {(() => {
                                        const [y, m, d] = rider.birthday.split('-');
                                        return `${m}/${d}/${y}`;
                                      })()}
                                    </p>
                                  )}
                                  {rider.grade && (
                                    <p className="text-base text-primary-600">
                                      Grade: {rider.grade}
                                    </p>
                                  )}
                                  <p className="text-base text-primary-600">
                                    Category: {rider.category}
                                  </p>
                                  <p className="text-base text-primary-600">
                                    Group: {rider.group}
                                  </p>
                                </>
                              )}
                              {/* Race Registration Status - Student/Rider types only (Requirements: 14.1-14.6) */}
                              {rider.type !== 'Coach' && rider.category !== 'Coach' && raceEvents.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-primary-200">
                                  <p className="text-base font-semibold text-primary-900 mb-1">Race Registration:</p>
                                  <RaceRegistrationStatus
                                    raceRegistrations={rider.race_registrations || {}}
                                    raceEvents={raceEvents}
                                    className="text-base text-primary-600"
                                  />
                                </div>
                              )}
                              {/* Time Trials */}
                              {rider.time_trials && rider.time_trials.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-primary-200">
                                  <p className="text-base font-semibold text-primary-900 mb-1">Time-Trials:</p>
                                  {rider.time_trials
                                    .sort((a: any, b: any) => {
                                      // Sort by date (newest first)
                                      return new Date(b.date).getTime() - new Date(a.date).getTime();
                                    })
                                    .slice(0, 5)
                                    .map((trial: any, index: number) => (
                                    <div key={index} className="text-base text-primary-600 mb-2">
                                      <p className="font-semibold text-primary-800">{trial.trail_name || 'N/A'}{trial.direction ? ` - ${trial.direction}` : ''}</p>
                                      <p>Date: {trial.date}</p>
                                      <p>Time: {trial.result}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Race Results */}
                              {rider.race_results && rider.race_results.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-primary-200">
                                  <p className="text-base font-semibold text-primary-900 mb-1">Race Results:</p>
                                  {rider.race_results
                                    .map((result: any) => ({
                                      result,
                                      formatted: formatRaceResult(result),
                                    }))
                                    .sort((a: { formatted: { eventDate: string } }, b: { formatted: { eventDate: string } }) => b.formatted.eventDate.localeCompare(a.formatted.eventDate))
                                    .slice(0, 5)
                                    .map(({ formatted }: { formatted: { eventDate: string; eventName: string; position: string } }, index: number) => {
                                      const positionDisplay = formatted.position.replace(' / ', ' out of ');
                                      return (
                                        <div key={index} className="text-base text-primary-600 mb-2">
                                          <p className="font-semibold text-primary-800">{formatted.eventName}</p>
                                          <p>Date: {formatted.eventDate}</p>
                                          <p>Result: {positionDisplay}</p>
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </section>

            {/* Leaderboard Preview */}
            <section aria-labelledby="leaderboard-heading">
              <Card>
                <div className="flex justify-between items-center mb-4">
                  <h2 id="leaderboard-heading" className="text-2xl font-semibold text-primary-900">
                    Leaderboard
                  </h2>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/leaderboard')}
                    className="text-base px-4 py-2 min-h-[44px]"
                    aria-label="View full leaderboard"
                  >
                    View Full
                  </Button>
                </div>

                {leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-primary-600" role="status">
                    <svg 
                      className="w-16 h-16 mx-auto mb-4 text-primary-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" 
                      />
                    </svg>
                    <p className="text-base">No leaderboard data available</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <LeaderboardSection title="Students" entries={leaderboardStudents} />
                    <LeaderboardSection title="Coaches" entries={leaderboardCoaches} />
                  </div>
                )}
              </Card>
            </section>
          </div>
        )}
      </main>

      {skillsRider && (
        <SkillsPanel
          rider={skillsRider}
          onClose={() => {
            setSkillsRider(null);
            setSkillsRefreshTrigger(prev => prev + 1);
          }}
        />
      )}
      
      {/* Cancel Event Modal */}
      {showCancelModal && eventToCancel && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeCancelModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleCancelEvent}>
              {/* Modal Header */}
              <div className="bg-primary-900 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
                <h3 className="text-xl font-semibold">Cancel Event</h3>
                <button
                  type="button"
                  onClick={closeCancelModal}
                  className="text-white hover:text-gray-200 text-2xl leading-none"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="px-6 py-4">
                <p className="mb-2">Are you sure you want to cancel this event?</p>
                <p className="text-sm text-blue-600 mb-4">
                  This will mark the event as canceled and send email notifications to all users.
                </p>
                
                <div>
                  <label htmlFor="cancellation_reason" className="block mb-2 font-semibold text-primary-900">
                    Reason for cancellation (optional):
                  </label>
                  <textarea
                    id="cancellation_reason"
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-vertical"
                    rows={3}
                    placeholder="Enter the reason for canceling this event (this will be included in the email notification)"
                  />
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="bg-gray-100 px-6 py-4 rounded-b-lg flex justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeCancelModal}
                  disabled={cancellingEvent}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="danger"
                  disabled={cancellingEvent}
                >
                  {cancellingEvent ? 'Processing...' : 'Cancel Event & Send Notifications'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
